import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type Tier, type Tenant, type HostJob, type SharedRulesCopy } from '@/lib/api';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, Label } from '@/components/ui/input';
import { Dialog } from '@/components/ui/dialog';
import { Toggle } from '@/components/ui/toggle';
import { DeleteModeOption } from '@/components/DeleteModeOption';
import { formatDate, relativeTime } from '@/lib/utils';

type ResetResult = {
  tier: string;
  tenants_total: number;
  tenants_ok: number;
  tenants_failed: number;
  licenses_reset: number;
  results: { tenant_id: string; ok: boolean; count?: number; reason?: string }[];
};

// Operator tools live here. Each tool is a self-contained card that gates its
// action behind a confirmation dialog. Add more cards to <ToolsPage> over time.
export function ToolsPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Tools</h1>
        <p className="text-sm text-slate-500 mt-1">
          One-off fleet maintenance actions. Each runs immediately after you confirm.
        </p>
      </div>
      <SiteRecoveryTool />
      <MoveSiteTool />
      <ResetTierActivationLimitsTool />
      <RemoveMissingTenantsTool />
    </div>
  );
}

const formatBytes = (n: number) => {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  return `${(n / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

// What the operator sees while a move runs. The two-pass cross-server flow is
// the reason these are worth naming: "copying" can last an hour with nothing
// down, and "cutting over" is the short window that isn't.
const MOVE_PHASES: Record<string, string> = {
  preflight: 'Checking both servers',
  shared_rules: 'Copying shared rules',
  release: 'Releasing the website from the old tenant',
  copy: 'Copying data',
  cutover: 'Cutting over — copying what changed',
  publish: 'Publishing on the new tenant',
  trash: 'Retiring the old copy',
  restart: 'Restarting the old tenant',
  done: 'Done',
};

// Move one website's orchestrator data to another tenant: its data dir plus the
// Claude/Codex session transcripts that make its chats resumable. The site is
// released from the old tenant (freeing its license seat); its data there is
// kept by default so the move stays reversible.
//
// The site does NOT start talking to the new tenant on its own — the
// orchestrator URL lives in settings.json inside the plugin folder, so the
// plugin has to be reinstalled from the new tenant's download URL. That's the
// step the operator has to do by hand afterwards, so the UI says so twice.
function MoveSiteTool() {
  const tenants = useQuery({
    queryKey: ['tenants'],
    queryFn: () => api.get<{ tenants: Tenant[] }>('/api/tenants'),
  });
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [removeSource, setRemoveSource] = useState(false);
  const [copyRules, setCopyRules] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  // The site pickers re-derive from a list that no longer contains the moved
  // site, so the result message has to read from this snapshot — otherwise it
  // names whichever website happens to be first in the refreshed list.
  const [submittedSiteUrl, setSubmittedSiteUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const tenantList = (tenants.data?.tenants ?? []).filter((t) => t.status !== 'deleted');
  const from = fromId || tenantList[0]?.id || '';
  const fromTenant = tenantList.find((t) => t.id === from) ?? null;

  const targets = tenantList.filter((t) => t.id !== from);
  const to = toId && targets.some((t) => t.id === toId) ? toId : targets[0]?.id || '';
  const toTenant = targets.find((t) => t.id === to) ?? null;
  const crossServer = !!fromTenant && !!toTenant && fromTenant.server_id !== toTenant.server_id;

  const sites = useQuery({
    queryKey: ['tenant-sites', from],
    queryFn: () =>
      api.get<{ sites: TenantSite[] }>(`/api/tools/tenant-sites?tenant=${encodeURIComponent(from)}`),
    enabled: !!from,
  });
  const siteList = sites.data?.sites ?? [];
  const site = siteId && siteList.some((s) => s.id === siteId) ? siteId : siteList[0]?.id || '';
  const siteUrl = siteList.find((s) => s.id === site)?.url ?? site;

  // A cross-server copy outlives the page, so the job row — not this component
  // — is what the move's state lives in, and a reload picks the running one
  // back up instead of losing sight of a copy that is still going. Only a
  // RUNNING job is adopted: a finished one belongs to whoever was watching it.
  const openJobs = useQuery({
    queryKey: ['host-jobs', 'move_site'],
    queryFn: () => api.get<{ jobs: HostJob[] }>('/api/tools/jobs?kind=move_site&limit=5'),
    refetchOnWindowFocus: false,
  });
  const adoptable = openJobs.data?.jobs.find((j) => j.status === 'running');
  useEffect(() => {
    if (!jobId && adoptable) setJobId(adoptable.id);
  }, [jobId, adoptable]);

  const job = useQuery({
    queryKey: ['host-job', jobId],
    queryFn: () => api.get<{ job: HostJob }>(`/api/tools/jobs/${jobId}`),
    enabled: !!jobId,
    refetchInterval: (q) => (q.state.data?.job.status === 'running' ? 2000 : false),
  });
  const active = job.data?.job ?? null;
  const running = !!active && active.status === 'running';
  const result = active?.status === 'succeeded' ? active.result : null;

  const reset = () => {
    setJobId(null);
    setError(null);
  };

  // On the first tick after a move lands, every cached site list is wrong: the
  // site just left the source tenant's registry and joined nothing yet (it
  // registers on the target only once its plugin is reinstalled).
  const finished = active && active.status !== 'running';
  useEffect(() => {
    if (finished) queryClient.invalidateQueries({ queryKey: ['tenant-sites'] });
  }, [finished, queryClient]);

  const run = useMutation({
    mutationFn: async () => {
      setSubmittedSiteUrl(siteUrl);
      return api.post<{ job_id: string }>('/api/tools/move-site', {
        site_id: site,
        from_tenant: from,
        to_tenant: to,
        remove_source_data: removeSource,
        copy_shared_rules: copyRules,
      });
    },
    onSuccess: (data) => {
      setJobId(data.job_id);
      setError(null);
      setConfirmOpen(false);
      queryClient.invalidateQueries({ queryKey: ['host-jobs', 'move_site'] });
    },
    onError: (err: Error) => {
      setError(err.message);
      setConfirmOpen(false);
    },
  });

  const busy = run.isPending || running;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Move a website to another tenant</CardTitle>
      </CardHeader>
      <CardBody className="space-y-4">
        <p className="text-sm text-slate-600">
          Copies a website's orchestrator data — its data dir plus the Claude/Codex session
          transcripts its chats resume from — to another tenant, and releases it from the old one so
          its license activation seat is freed. The two tenants can be on different servers.
        </p>
        <p className="text-sm text-slate-600">
          <b>This does not re-point the website.</b> The orchestrator URL is baked into the plugin
          folder, so the site keeps talking to the old tenant until its plugin is reinstalled from
          the new tenant's download URL. Nothing in WordPress is touched here.
        </p>
        {error && <div className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</div>}
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[200px]">
            <Label htmlFor="move-from">From tenant</Label>
            <Select
              id="move-from"
              value={from}
              onChange={(e) => {
                setFromId(e.target.value);
                setToId('');
                setSiteId('');
                reset();
              }}
              disabled={tenants.isLoading || tenantList.length === 0 || busy}
            >
              {tenantList.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.id} ({t.server_id})
                </option>
              ))}
            </Select>
          </div>
          <div className="min-w-[280px]">
            <Label htmlFor="move-site">Website</Label>
            <Select
              id="move-site"
              value={site}
              onChange={(e) => {
                setSiteId(e.target.value);
                reset();
              }}
              disabled={sites.isLoading || siteList.length === 0 || busy}
            >
              {siteList.map((s) => (
                <option key={s.id} value={s.id}>
                  {siteLabel(s)}
                </option>
              ))}
            </Select>
          </div>
          <div className="min-w-[200px]">
            <Label htmlFor="move-to">To tenant</Label>
            <Select
              id="move-to"
              value={to}
              onChange={(e) => {
                setToId(e.target.value);
                reset();
              }}
              disabled={targets.length === 0 || busy}
            >
              {targets.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.id} ({t.server_id})
                </option>
              ))}
            </Select>
          </div>
          <Button
            onClick={() => {
              setError(null);
              setConfirmOpen(true);
            }}
            disabled={!site || !to || busy}
          >
            {busy ? 'Moving…' : 'Move website'}
          </Button>
        </div>

        <p className="text-sm text-slate-600">
          {crossServer ? (
            <>
              <b>{fromTenant?.server_id}</b> → <b>{toTenant?.server_id}</b>: the bulk of the data is
              copied while both tenants keep running, then both are briefly stopped to copy what
              changed and publish. Expect seconds of downtime at the end, not the whole copy.
            </>
          ) : (
            <>
              Both tenants are on <b>{fromTenant?.server_id}</b>. They are stopped for the copy and
              restarted afterwards (a suspended tenant stays suspended), so expect them to be
              briefly offline.
            </>
          )}
        </p>

        <div className="flex items-start gap-2.5">
          <Toggle
            checked={removeSource}
            onChange={(v) => {
              setRemoveSource(v);
              reset();
            }}
            label="Also delete the website's data on the old tenant"
          />
          <div className="text-sm">
            <div className="text-slate-700">Also delete the website's data on the old tenant</div>
            <div className="text-slate-500 text-xs mt-0.5">
              {removeSource ? (
                <>
                  The old copy moves into that tenant's <code>.trash</code>, which its orchestrator
                  sweeps after 90 days. Recoverable until then, but the move is no longer a simple
                  plugin reinstall away from being undone.
                </>
              ) : (
                <>
                  Off: the old copy stays on disk untouched. Reinstalling the old tenant's plugin
                  puts the site right back on it, with all its history.
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-start gap-2.5">
          <Toggle
            checked={copyRules}
            onChange={(v) => {
              setCopyRules(v);
              reset();
            }}
            label="Also copy shared rules"
          />
          <div className="text-sm">
            <div className="text-slate-700">Also copy shared rules</div>
            <div className="text-slate-500 text-xs mt-0.5">
              {copyRules ? (
                <>
                  The old tenant's “All my websites” rules that are on for this website are added to
                  the new tenant, where they apply to all of its websites. Rules it already has are
                  left as they are. Both tenants must be running.
                </>
              ) : (
                <>Off: the website leaves the old tenant's “All my websites” rules behind.</>
              )}
            </div>
          </div>
        </div>

        {sites.isError && (
          <div className="text-xs text-amber-700 bg-amber-50 rounded-md px-3 py-2">
            Could not list this tenant's sites — is the tenant container running?
          </div>
        )}
        {!!fromTenant && targets.length === 0 && (
          <div className="text-xs text-amber-700 bg-amber-50 rounded-md px-3 py-2">
            There is only one tenant. Create another to move a website to it.
          </div>
        )}

        {active && <MoveJobPanel job={active} />}
        {result && (
          <div className="text-sm text-slate-900 bg-slate-50 rounded-md px-3 py-2">
            Next: reinstall the Godmode plugin on <b>{submittedSiteUrl || result.site_id}</b> from{' '}
            <b>{result.to}</b>'s download URL. Until then the site still talks to the old tenant.
          </div>
        )}
      </CardBody>

      <Dialog
        open={confirmOpen}
        onOpenChange={(v) => !run.isPending && setConfirmOpen(v)}
        title="Move this website?"
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={run.isPending}>
              Cancel
            </Button>
            <Button onClick={() => run.mutate()} disabled={run.isPending}>
              {run.isPending ? 'Moving…' : 'Move'}
            </Button>
          </>
        }
      >
        <div className="text-sm text-slate-700 space-y-2">
          <p>
            Moves <b>{siteUrl}</b> from tenant <b>{from}</b> to <b>{to}</b>
            {crossServer ? (
              <>
                , across from server <b>{fromTenant?.server_id}</b> to <b>{toTenant?.server_id}</b>.
                Both keep running for the bulk copy and are stopped only for the cutover.
              </>
            ) : (
              <>. Both containers stop for the copy and restart when it's done.</>
            )}
          </p>
          <p>
            {removeSource ? (
              <>
                Its data on <b>{from}</b> will be <b>trashed</b> (recoverable there for 90 days).
              </>
            ) : (
              <>
                Its data on <b>{from}</b> will be <b>kept</b>, so the move can be undone by
                reinstalling that tenant's plugin.
              </>
            )}
          </p>
          {copyRules && (
            <p>
              Its shared rules are copied to <b>{to}</b> first, where they apply to all of its
              websites.
            </p>
          )}
          <p>
            Afterwards you still have to reinstall the plugin on the site from <b>{to}</b>'s
            download URL — nothing points the site at its new tenant until you do.
          </p>
        </div>
      </Dialog>
    </Card>
  );
}

// Live state of one move. The same panel covers a same-server move that takes
// ten seconds and a cross-server one that takes an hour.
function MoveJobPanel({ job }: { job: HostJob }) {
  const phase = MOVE_PHASES[job.phase] ?? job.phase;
  const { pass, pct, bytes_total: total } = job.progress ?? {};
  const result = job.result;

  const tone =
    job.status === 'failed' || job.status === 'interrupted'
      ? 'text-red-700 bg-red-50'
      : job.status === 'succeeded'
        ? 'text-slate-700 bg-slate-50'
        : 'text-slate-700 bg-slate-50';

  return (
    <div className={`text-sm rounded-md px-3 py-2 space-y-1 ${tone}`}>
      {job.status === 'running' && (
        <div>
          <b>{phase}</b>
          {job.phase === 'copy' && !!total && <> — {formatBytes(total)} to copy</>}
          {typeof pct === 'number' && pct > 0 && pct < 100 && <> · {pct}%</>}
          {pass === 2 && <> (second pass)</>}
        </div>
      )}
      {(job.status === 'failed' || job.status === 'interrupted') && (
        <div>
          <b>The move did not finish.</b> {job.error_message}
        </div>
      )}
      {job.status === 'succeeded' && result && (
        <>
          <div>
            Moved <b>{formatBytes(result.site_bytes)}</b> of site data from <b>{result.from}</b> to{' '}
            <b>{result.to}</b>, with {result.claude_sessions} Claude session
            {result.claude_sessions === 1 ? '' : 's'} and {result.codex_rollouts} Codex rollout
            {result.codex_rollouts === 1 ? '' : 's'}.
          </div>
          <div>
            {result.purged_source ? (
              <>
                The old copy was trashed at <code className="text-xs">{result.trash_entry}</code>.
              </>
            ) : (
              <>The old copy was left in place on {result.from}.</>
            )}
          </div>
          {result.replaced_transcripts > 0 && (
            <div>
              {result.to} still had {result.replaced_transcripts} older transcript
              {result.replaced_transcripts === 1 ? '' : 's'} of this website from an earlier stay
              there. The incoming ones replaced {result.replaced_transcripts === 1 ? 'it' : 'them'};
              the old copies are in its trash at{' '}
              <code className="text-xs">{result.replaced_entry}</code>.
            </div>
          )}
          {result.shared_rules && <SharedRulesSummary copy={result.shared_rules} to={result.to} />}
        </>
      )}
      {(job.pending_recovery ?? []).length > 0 && (
        <div className="text-amber-700">
          Still being undone: {job.pending_recovery.join('; ')}. The control plane retries this every
          few minutes; these tenants can be moved again once it's done.
        </div>
      )}
      {(job.warnings ?? []).map((w) => (
        <div key={w} className="text-amber-700">
          {w}
        </div>
      ))}
      {job.status !== 'running' && !result && job.log_tail && (
        <pre className="text-xs whitespace-pre-wrap text-slate-600 max-h-40 overflow-y-auto">
          {job.log_tail.slice(-1200)}
        </pre>
      )}
    </div>
  );
}

// What copying the shared rules did. A rule the new tenant kept its own version
// of is amber: the moved website now follows that version, not the one it had.
function SharedRulesSummary({ copy, to }: { copy: SharedRulesCopy; to: string }) {
  const quoted = (names: string[]) => names.map((n) => `“${n}”`).join(', ');
  const exists = copy.skipped.filter((s) => s.reason === 'exists').map((s) => s.name);
  const taken = copy.skipped.filter((s) => s.reason === 'name_taken').map((s) => s.name);
  const invalid = copy.skipped.filter((s) => s.reason === 'invalid');
  const nothing = copy.added.length === 0 && copy.skipped.length === 0 && copy.switched_off === 0;

  return (
    <>
      <div>
        {nothing ? (
          <>The old tenant has no shared rules to copy.</>
        ) : copy.added.length > 0 ? (
          <>
            Copied {copy.added.length} shared rule{copy.added.length === 1 ? '' : 's'} to <b>{to}</b>:{' '}
            {quoted(copy.added)}.
          </>
        ) : (
          <>No shared rules were added to <b>{to}</b>.</>
        )}
        {exists.length > 0 && <> Already there: {quoted(exists)}.</>}
        {copy.switched_off > 0 && (
          <>
            {' '}
            {copy.switched_off} rule{copy.switched_off === 1 ? '' : 's'} switched off for this website
            stayed behind.
          </>
        )}
      </div>
      {taken.length > 0 && (
        <div className="text-amber-700">
          Not copied: {quoted(taken)}. {to} already has a different shared rule with that name and
          kept its own.
        </div>
      )}
      {invalid.map((s) => (
        <div key={s.name} className="text-amber-700">
          Not copied: “{s.name}”. {(s.message || `${to} could not store it`).replace(/\.$/, '')}.
        </div>
      ))}
    </>
  );
}

// Shape of GET /api/tools/tenant-sites — shared by every site-scoped tool here.
type TenantSite = { id: string; url: string; registeredAt: string | null };

const siteLabel = (s: TenantSite) => `${s.url.replace(/^https?:\/\//, '')} (${s.id.slice(0, 8)}…)`;

type RecoveryJob = {
  jobId: string;
  siteId: string;
  phase: string;
  startedAt: string;
  finishedAt: string | null;
  error: string | null;
  report: {
    chats: { total: number; skeletons: number; existing: number };
    mirrors: { total: number; initialized: number; alreadyPresent: number; failed: number; listError?: string };
    transcripts: {
      sessions: number;
      matched: number;
      rebuilt: number;
      relinked: number;
      ambiguous: unknown[];
      unmatchedSessions?: unknown[];
      emptyChats: number;
    };
  };
};

// Rebuild a site's orchestrator-side data after a data loss: skeleton
// conversations from the WordPress chat rows, folder mirrors re-imported from
// wp-content, and chat histories reconstructed from Claude/Codex session
// transcripts. Strictly additive — existing chat data is never overwritten.
function SiteRecoveryTool() {
  const tenants = useQuery({
    queryKey: ['tenants'],
    queryFn: () => api.get<{ tenants: Tenant[] }>('/api/tenants'),
  });
  const [tenantId, setTenantId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [extraIds, setExtraIds] = useState<string[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [activeJobSite, setActiveJobSite] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const tenantList = (tenants.data?.tenants ?? []).filter((t) => t.status !== 'deleted');
  const selectedTenant = tenantId || tenantList[0]?.id || '';

  const sites = useQuery({
    queryKey: ['tenant-sites', selectedTenant],
    queryFn: () =>
      api.get<{ sites: TenantSite[] }>(`/api/tools/tenant-sites?tenant=${encodeURIComponent(selectedTenant)}`),
    enabled: !!selectedTenant,
  });
  const siteList = sites.data?.sites ?? [];
  const selectedSite = siteId && siteList.some((s) => s.id === siteId) ? siteId : siteList[0]?.id || '';
  const otherSites = siteList.filter((s) => s.id !== selectedSite);

  const status = useQuery({
    queryKey: ['site-recovery-status', selectedTenant, activeJobSite],
    queryFn: () =>
      api.get<{ job: RecoveryJob }>(
        `/api/tools/site-recovery-status?tenant=${encodeURIComponent(selectedTenant)}&site_id=${encodeURIComponent(activeJobSite!)}`
      ),
    enabled: !!selectedTenant && !!activeJobSite,
    refetchInterval: (q) => (q.state.data?.job?.finishedAt ? false : 2000),
  });
  const job = status.data?.job ?? null;

  const run = useMutation({
    mutationFn: () =>
      api.post<{ job_id: string; already_running: boolean }>('/api/tools/site-recovery', {
        tenant: selectedTenant,
        site_id: selectedSite,
        extra_site_ids: extraIds,
      }),
    onSuccess: () => {
      setError(null);
      setConfirmOpen(false);
      setActiveJobSite(selectedSite);
    },
    onError: (err: Error) => {
      setError(err.message);
      setConfirmOpen(false);
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recover a website's data</CardTitle>
      </CardHeader>
      <CardBody className="space-y-4">
        <p className="text-sm text-slate-600">
          Rebuilds a site's orchestrator data after a loss: every WordPress chat gets its
          conversation back (skeleton at minimum), folder mirrors are re-imported from wp-content,
          and chat histories are reconstructed from the Claude/Codex session transcripts on the
          tenant. Strictly read-only toward WordPress — nothing is written, deployed, or
          version-bumped on the site — and existing orchestrator chat data is never overwritten.
        </p>
        {error && <div className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</div>}
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px]">
            <Label htmlFor="recovery-tenant">Tenant</Label>
            <Select
              id="recovery-tenant"
              value={selectedTenant}
              onChange={(e) => {
                setTenantId(e.target.value);
                setSiteId('');
                setExtraIds([]);
                setActiveJobSite(null);
              }}
              disabled={tenants.isLoading || tenantList.length === 0}
            >
              {tenantList.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.id}
                </option>
              ))}
            </Select>
          </div>
          <div className="min-w-[280px]">
            <Label htmlFor="recovery-site">Website</Label>
            <Select
              id="recovery-site"
              value={selectedSite}
              onChange={(e) => {
                setSiteId(e.target.value);
                setExtraIds([]);
                setActiveJobSite(null);
              }}
              disabled={sites.isLoading || siteList.length === 0}
            >
              {siteList.map((s) => (
                <option key={s.id} value={s.id}>
                  {siteLabel(s)}
                </option>
              ))}
            </Select>
          </div>
          <Button
            onClick={() => { setError(null); setConfirmOpen(true); }}
            disabled={!selectedSite || run.isPending || (!!job && !job.finishedAt)}
          >
            Recover site
          </Button>
        </div>

        {otherSites.length > 0 && (
          <div className="space-y-1">
            <Label>Also scan transcripts stored under these site ids (clone siblings)</Label>
            <div className="flex flex-wrap gap-3">
              {otherSites.map((s) => (
                <label key={s.id} className="flex items-center gap-1.5 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={extraIds.includes(s.id)}
                    onChange={(e) =>
                      setExtraIds((prev) =>
                        e.target.checked ? [...prev, s.id] : prev.filter((id) => id !== s.id)
                      )
                    }
                  />
                  {siteLabel(s)}
                </label>
              ))}
            </div>
          </div>
        )}

        {sites.isError && (
          <div className="text-xs text-amber-700 bg-amber-50 rounded-md px-3 py-2">
            Could not list this tenant's sites — is the tenant container running?
          </div>
        )}

        {job && (
          <div className="text-sm text-slate-700 bg-slate-50 rounded-md px-3 py-2 space-y-1">
            {!job.finishedAt && (
              <div>
                Running… phase: <b>{job.phase}</b>
              </div>
            )}
            {job.finishedAt && job.error && (
              <div className="text-red-600">Failed in phase {job.phase}: {job.error}</div>
            )}
            {job.finishedAt && !job.error && (
              <>
                <div>
                  Done. <b>{job.report.chats.skeletons}</b> chat skeleton
                  {job.report.chats.skeletons === 1 ? '' : 's'} created (
                  {job.report.chats.existing} already had data),{' '}
                  <b>{job.report.mirrors.initialized}</b> mirror
                  {job.report.mirrors.initialized === 1 ? '' : 's'} re-imported,{' '}
                  <b>{job.report.transcripts.rebuilt}</b> chat histor
                  {job.report.transcripts.rebuilt === 1 ? 'y' : 'ies'} rebuilt from{' '}
                  {job.report.transcripts.sessions} transcript session
                  {job.report.transcripts.sessions === 1 ? '' : 's'} (
                  {job.report.transcripts.relinked} session{job.report.transcripts.relinked === 1 ? '' : 's'} relinked for resume).
                </div>
                {job.report.mirrors.failed > 0 && (
                  <div className="text-red-600">
                    {job.report.mirrors.failed} mirror import{job.report.mirrors.failed === 1 ? '' : 's'} failed — see the tenant log.
                  </div>
                )}
                {job.report.mirrors.listError && (
                  <div className="text-red-600">
                    Folder list could not be fetched from WordPress ({job.report.mirrors.listError}) — no mirrors were re-imported.
                  </div>
                )}
                {job.report.transcripts.ambiguous.length > 0 && (
                  <div className="text-amber-700">
                    {job.report.transcripts.ambiguous.length} transcript session
                    {job.report.transcripts.ambiguous.length === 1 ? '' : 's'} matched more than one
                    chat and {job.report.transcripts.ambiguous.length === 1 ? 'was' : 'were'} left
                    untouched — details in the site's recovery-report.json.
                  </div>
                )}
                {(job.report.transcripts.unmatchedSessions?.length ?? 0) > 0 && (
                  <div className="text-slate-500">
                    {job.report.transcripts.unmatchedSessions!.length} session
                    {job.report.transcripts.unmatchedSessions!.length === 1 ? '' : 's'} matched no
                    chat (sessions of chats that already have data, or no preview overlap) — listed
                    in recovery-report.json.
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </CardBody>

      <Dialog
        open={confirmOpen}
        onOpenChange={(v) => !run.isPending && setConfirmOpen(v)}
        title="Recover this site?"
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={run.isPending}>
              Cancel
            </Button>
            <Button onClick={() => run.mutate()} disabled={run.isPending}>
              {run.isPending ? 'Starting…' : 'Recover'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-700">
          Runs the recovery job on tenant <b>{selectedTenant}</b> for{' '}
          <b>{siteList.find((s) => s.id === selectedSite)?.url ?? selectedSite}</b>. The job is
          strictly additive — chats that already have history are left untouched. Reconstructed
          messages are flagged as recovered in the chat UI.
        </p>
      </Dialog>
    </Card>
  );
}

function ResetTierActivationLimitsTool() {
  const tiers = useQuery({
    queryKey: ['tiers'],
    queryFn: () => api.get<{ tiers: Tier[] }>('/api/tiers'),
  });
  const [tier, setTier] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<ResetResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const list = tiers.data?.tiers ?? [];
  // Fall back to the first tier until the operator picks one.
  const selected = tier || list[0]?.id || '';
  const selectedTier = list.find((t) => t.id === selected) ?? null;

  const run = useMutation({
    mutationFn: () =>
      api.post<ResetResult>('/api/tools/reset-tier-activation-limits', { tier: selected }),
    onSuccess: (data) => {
      setResult(data);
      setError(null);
      setConfirmOpen(false);
    },
    onError: (err: Error) => {
      setError(err.message);
      setConfirmOpen(false);
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Reset license activation limits for a tier</CardTitle>
      </CardHeader>
      <CardBody className="space-y-4">
        <p className="text-sm text-slate-600">
          Clears the activation-limit override on every license key for all tenants on the selected
          tier — each key reverts to blank (inherit), following whatever default its tenant resolves.
        </p>
        {error && <div className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</div>}
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[240px]">
            <Label htmlFor="reset-tier">Tier</Label>
            <Select
              id="reset-tier"
              value={selected}
              onChange={(e) => {
                setTier(e.target.value);
                setResult(null);
              }}
              disabled={tiers.isLoading || list.length === 0}
            >
              {list.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label} ({t.tenant_count} tenant{t.tenant_count === 1 ? '' : 's'})
                </option>
              ))}
            </Select>
          </div>
          <Button onClick={() => { setError(null); setConfirmOpen(true); }} disabled={!selected || run.isPending}>
            Reset activation limits
          </Button>
        </div>

        {result && (
          <div className="text-sm text-slate-700 bg-slate-50 rounded-md px-3 py-2 space-y-1">
            <div>
              Reset <b>{result.licenses_reset}</b> license key{result.licenses_reset === 1 ? '' : 's'}{' '}
              across <b>{result.tenants_ok}</b> tenant{result.tenants_ok === 1 ? '' : 's'}.
            </div>
            {result.tenants_failed > 0 && (
              <div className="text-red-600">
                {result.tenants_failed} tenant{result.tenants_failed === 1 ? '' : 's'} could not be
                reached — run it again to cover them.
              </div>
            )}
          </div>
        )}
      </CardBody>

      <Dialog
        open={confirmOpen}
        onOpenChange={(v) => !run.isPending && setConfirmOpen(v)}
        title="Reset activation limits?"
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={run.isPending}>
              Cancel
            </Button>
            <Button onClick={() => run.mutate()} disabled={run.isPending}>
              {run.isPending ? 'Resetting…' : 'Reset'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-700">
          This clears the activation-limit override on <b>every license key</b> for all{' '}
          <b>{selectedTier?.tenant_count ?? 0}</b> tenant
          {(selectedTier?.tenant_count ?? 0) === 1 ? '' : 's'} on the{' '}
          <b>{selectedTier?.label ?? selected}</b> tier. Each key reverts to inheriting its tenant's
          default. This can't be undone.
        </p>
      </Dialog>
    </Card>
  );
}

type DeleteMode = 'stop' | 'stop_purge' | 'cp_only';

type MissingTenant = Pick<
  Tenant,
  'id' | 'server_id' | 'customer_email' | 'company_name' | 'status' | 'created_at' | 'last_chat_activity_at'
>;

type SkipReason =
  | 'provisioning'
  | 'container_present'
  | 'server_unreachable'
  | 'server_not_found'
  | 'not_missing'
  | 'not_found';

type SkippedTenant = { tenant_id: string; reason: SkipReason; detail?: string };

type MissingPreview = { tenants: MissingTenant[]; skipped: SkippedTenant[] };

type RemoveMissingResult = {
  mode: DeleteMode;
  removed: string[];
  failed: { tenant_id: string; reason: string }[];
  skipped: SkippedTenant[];
};

const SKIP_LABELS: Record<SkipReason, string> = {
  provisioning: 'still being provisioned',
  container_present: 'container is there now',
  server_unreachable: "server didn't answer the live check",
  server_not_found: 'server not found',
  not_missing: 'no longer reported missing',
  not_found: 'already deleted',
};

function SkippedList({ items }: { items: SkippedTenant[] }) {
  return (
    <div className="text-xs text-slate-600 space-y-0.5">
      <div className="font-medium text-slate-700">
        Skipped {items.length} tenant{items.length === 1 ? '' : 's'}:
      </div>
      {items.map((s) => (
        <div key={s.tenant_id}>
          <span className="font-mono">{s.tenant_id}</span> — {SKIP_LABELS[s.reason]}
          {s.detail ? ` (${s.detail})` : ''}
        </div>
      ))}
    </div>
  );
}

// Clears out tenants whose container no longer exists on their server — e.g.
// containers removed by hand on the host, leaving the CP rows behind. The
// server re-checks every tenant live before listing it and again right before
// removing it, so a tenant caught mid-recycle is skipped, not deleted.
function RemoveMissingTenantsTool() {
  const qc = useQueryClient();
  const [preview, setPreview] = useState<MissingPreview | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<DeleteMode>('stop');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<RemoveMissingResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const find = useMutation({
    mutationFn: () => api.get<MissingPreview>('/api/tools/missing-tenants'),
    onSuccess: (data) => {
      setPreview(data);
      setSelected(new Set(data.tenants.map((t) => t.id)));
      setError(null);
    },
    onError: (err: Error) => setError(err.message),
  });

  const remove = useMutation({
    mutationFn: () =>
      api.post<RemoveMissingResult>('/api/tools/remove-missing-tenants', {
        mode,
        tenant_ids: [...selected],
      }),
    onSuccess: (data) => {
      setResult(data);
      setPreview(null);
      setSelected(new Set());
      setError(null);
      setConfirmOpen(false);
      qc.invalidateQueries({ queryKey: ['tenants'] });
    },
    onError: (err: Error) => {
      setError(err.message);
      setConfirmOpen(false);
    },
  });

  const tenants = preview?.tenants ?? [];
  const allSelected = tenants.length > 0 && tenants.every((t) => selected.has(t.id));
  const toggle = (id: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  const count = selected.size;
  const plural = count === 1 ? '' : 's';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Remove tenants with missing containers</CardTitle>
      </CardHeader>
      <CardBody className="space-y-4">
        <p className="text-sm text-slate-600">
          Lists tenants the health check reports as <b>missing</b> — no container on their server —
          so you can remove them in one go. Each tenant is re-checked live on its server before it's
          listed and again right before it's removed; any whose container is back, that's still
          being provisioned, or whose server doesn't answer is skipped.
        </p>
        {error && <div className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</div>}
        <Button
          onClick={() => {
            setResult(null);
            find.mutate();
          }}
          disabled={find.isPending || remove.isPending}
        >
          {find.isPending ? 'Checking servers…' : preview ? 'Check again' : 'Find missing tenants'}
        </Button>

        {preview && tenants.length === 0 && (
          <div className="text-sm text-slate-700 bg-slate-50 rounded-md px-3 py-2">
            No tenants with a missing container.
          </div>
        )}

        {tenants.length > 0 && (
          <>
            <div className="max-h-80 overflow-auto rounded-md border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs text-slate-500">
                  <tr>
                    <th className="px-3 py-2 w-8">
                      <input
                        type="checkbox"
                        aria-label="Select all"
                        checked={allSelected}
                        onChange={(e) =>
                          setSelected(e.target.checked ? new Set(tenants.map((t) => t.id)) : new Set())
                        }
                      />
                    </th>
                    <th className="px-3 py-2 font-medium">Tenant</th>
                    <th className="px-3 py-2 font-medium">Customer</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium">Created</th>
                    <th className="px-3 py-2 font-medium">Last used</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {tenants.map((t) => (
                    <tr key={t.id}>
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          aria-label={`Select ${t.id}`}
                          checked={selected.has(t.id)}
                          onChange={(e) => toggle(t.id, e.target.checked)}
                        />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">
                        <Link to={`/tenants/${t.id}`} className="text-slate-900 hover:text-slate-700">
                          {t.id}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        {t.customer_email ?? '—'}
                        {t.company_name && <div className="text-xs text-slate-500">{t.company_name}</div>}
                      </td>
                      <td className="px-3 py-2 text-slate-700">{t.status}</td>
                      <td className="px-3 py-2 text-slate-700">
                        <span title={formatDate(t.created_at)}>{relativeTime(t.created_at)}</span>
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        {t.last_chat_activity_at ? relativeTime(t.last_chat_activity_at) : 'never'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="space-y-2">
              <Label>What to do with each selected tenant</Label>
              <DeleteModeOption
                value="stop"
                name="missing-delete-mode"
                checked={mode === 'stop'}
                onChange={setMode}
                title="Stop container"
                body={
                  <>
                    Runs <code className="font-mono text-[11px]">remove-tenant.sh</code> on the
                    tenant's server. Data and env file kept on disk. Tenant row soft-deleted.
                  </>
                }
              />
              <DeleteModeOption
                value="stop_purge"
                name="missing-delete-mode"
                checked={mode === 'stop_purge'}
                onChange={setMode}
                title="Stop & purge data"
                body={
                  <>
                    Adds <code className="font-mono text-[11px]">--purge-data</code> — wipes{' '}
                    <code className="font-mono text-[11px]">/var/godmode/tenants/&lt;id&gt;/</code>,{' '}
                    <code className="font-mono text-[11px]">/opt/godmode/tenants/&lt;id&gt;.env</code> and the
                    tenant's backups for every selected tenant. <b className="text-red-700">Unrecoverable.</b>
                  </>
                }
              />
              <DeleteModeOption
                value="cp_only"
                name="missing-delete-mode"
                checked={mode === 'cp_only'}
                onChange={setMode}
                title="CP record only"
                body={
                  <>
                    Hard-deletes the row in the control plane. Nothing on the host is touched. Frees
                    the tenant id for immediate re-provisioning.
                  </>
                }
              />
            </div>

            <Button
              variant="destructive"
              onClick={() => {
                setError(null);
                setConfirmOpen(true);
              }}
              disabled={count === 0 || remove.isPending}
            >
              Remove {count} tenant{plural}
            </Button>
          </>
        )}

        {preview && preview.skipped.length > 0 && <SkippedList items={preview.skipped} />}

        {result && (
          <div className="text-sm text-slate-700 bg-slate-50 rounded-md px-3 py-2 space-y-2">
            <div>
              Removed <b>{result.removed.length}</b> tenant{result.removed.length === 1 ? '' : 's'}.
            </div>
            {result.failed.length > 0 && (
              <div className="text-red-600 space-y-0.5">
                <div>
                  {result.failed.length} failed — run it again to retry:
                </div>
                {result.failed.map((f) => (
                  <div key={f.tenant_id} className="text-xs">
                    <span className="font-mono">{f.tenant_id}</span> — {f.reason}
                  </div>
                ))}
              </div>
            )}
            {result.skipped.length > 0 && <SkippedList items={result.skipped} />}
          </div>
        )}
      </CardBody>

      <Dialog
        open={confirmOpen}
        onOpenChange={(v) => !remove.isPending && setConfirmOpen(v)}
        title={`Remove ${count} tenant${plural}?`}
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={remove.isPending}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => remove.mutate()} disabled={remove.isPending}>
              {remove.isPending ? 'Removing…' : 'Remove'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-700">
          {mode === 'stop' && (
            <>
              Removes <b>{count}</b> tenant{plural}, keeping their data on disk.
            </>
          )}
          {mode === 'stop_purge' && (
            <>
              Removes <b>{count}</b> tenant{plural} and <b className="text-red-700">wipes their data</b>.
              This can't be undone.
            </>
          )}
          {mode === 'cp_only' && (
            <>
              Deletes <b>{count}</b> tenant row{plural} from the control plane. Nothing on the host is
              touched.
            </>
          )}{' '}
          Each one is checked on its server one last time first; any whose container has come back is
          skipped.
        </p>
      </Dialog>
    </Card>
  );
}
